import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Tickets' },
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'declined', label: 'Declined' },
  { value: 'free', label: 'Free' }
];

export default function AdminTicketList() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { status, page, limit: 20 };
      if (search) params.search = search;
      const res = await api.get('/admin/tickets', { params });
      setTickets(res.data.tickets);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } finally {
      setLoading(false);
    }
  }, [status, search, page]);

  useEffect(() => { setPage(1); }, [status, search]);
  useEffect(() => { load(); }, [load]);

  const formatDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

  const handleDelete = async (id, ticketNumber) => {
    if (!window.confirm(`Delete ticket ${ticketNumber}? This will also remove their payment receipt image.`)) return;
    try {
      await api.delete(`/admin/tickets/${id}`);
      setTickets(prev => prev.filter(t => t._id !== id));
      setTotal(prev => prev - 1);
    } catch (err) {
      alert(err.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <div>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Tickets</h1>
        <p className="admin-page-sub">{total} total submissions</p>
      </div>

      <div className="admin-toolbar">
        <input
          className="admin-search" placeholder="Search name, phone, ticket #..."
          value={search} onChange={e => setSearch(e.target.value)}
        />
        <select className="admin-select" value={status} onChange={e => setStatus(e.target.value)}>
          {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <div className="admin-table-wrap">
        <table className="admin-table">
          <thead>
            <tr>
              <th>Ticket #</th>
              <th>Name</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Check-in</th>
              <th>Submitted</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'rgba(255,255,255,0.3)' }}>Loading...</td></tr>
            ) : tickets.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', padding: 32, color: 'rgba(255,255,255,0.3)' }}>No tickets found</td></tr>
            ) : tickets.map(t => (
              <tr key={t._id}>
                <td>
                  <span className="admin-table-link" onClick={() => navigate(`/admin/tickets/${t._id}`)}>
                    {t.ticketNumber}
                  </span>
                </td>
                <td>{t.fullName}</td>
                <td>{t.phoneNumber}</td>
                <td><span className={`badge ${t.paymentStatus}`}>{t.paymentStatus}</span></td>
                <td>{t.checkedIn ? <span className="badge checked">✓ In</span> : '—'}</td>
                <td>{formatDate(t.createdAt)}</td>
                <td>
                  <button
                    className="admin-btn admin-btn-danger"
                    style={{ padding: '5px 12px', fontSize: 12 }}
                    onClick={() => handleDelete(t._id, t.ticketNumber)}
                  >
                    🗑 Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'center' }}>
          <button className="admin-btn admin-btn-ghost" onClick={() => setPage(p => p - 1)} disabled={page <= 1}>← Prev</button>
          <span style={{ padding: '10px 16px', fontSize: 14, color: 'rgba(255,255,255,0.4)' }}>Page {page} of {pages}</span>
          <button className="admin-btn admin-btn-ghost" onClick={() => setPage(p => p + 1)} disabled={page >= pages}>Next →</button>
        </div>
      )}
    </div>
  );
}
